import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import { AuthContext } from "../../../../context/AuthContext";
import ConfirmModal from "../../../../components/Modals/ConfirmModal";
import BackButton from "../../../../components/Button/BackButton";
import SystemValueList from "../../../../components/SystemValue/SystemValueList";
import { toast } from "../../../../components/Toast/toast";
import { btn } from "../../../../components/Button/buttonStyles";
import DefaultTaxForm from "./DefaultTaxForm";
import { formatTaxValue, taxTypeLabel } from "./taxFormat";

const COLUMNS = [
  { header: "Tax", render: (t) => t.name || "—" },
  { header: "Type", render: (t) => taxTypeLabel(t.type) },
  { header: "Default Value", render: (t) => formatTaxValue(t) },
];

const errorMessage = (err, fallback) =>
  err.response?.data?.error || err.response?.data?.message || fallback;

const byName = (a, b) => a.name.localeCompare(b.name);

const DefaultTaxesPage = ({ party, title }) => {
  const { user } = useContext(AuthContext);
  const canEdit = user?.roleSlug === "administrator";
  const endpoint = `/default-taxes/${party}`;

  const [taxes, setTaxes] = useState([]);
  const [accountTaxes, setAccountTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [taxToDelete, setTaxToDelete] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const action = canEdit ? searchParams.get("action") : null;
  const editId = searchParams.get("id");

  useEffect(() => {
    Promise.all([
      api.get(endpoint),
      api.get("/taxes"),
    ])
      .then(([taxRes, accountTaxRes]) => {
        setTaxes(taxRes.data.data);
        setAccountTaxes(accountTaxRes.data.data);
      })
      .catch((err) => setError(errorMessage(err, "Failed to fetch default taxes.")))
      .finally(() => setLoading(false));
  }, [endpoint]);

  const setAction = (nextAction, id) => {
    const next = new URLSearchParams(searchParams);
    if (nextAction) next.set("action", nextAction);
    else next.delete("action");
    if (id !== undefined && id !== null) next.set("id", String(id));
    else next.delete("id");
    setSearchParams(next, { replace: true });
  };

  const handleSave = async (payload) => {
    try {
      if (action === "edit") {
        const res = await api.put(`${endpoint}/${editId}`, payload);
        setTaxes((prev) =>
          prev.map((t) => (String(t.id) === String(editId) ? res.data.data : t)).sort(byName)
        );
      } else {
        const res = await api.post(endpoint, payload);
        setTaxes((prev) => [...prev, res.data.data].sort(byName));
      }
      toast.success(action === "edit" ? "Default tax updated." : "Default tax added.");
      setAction(null);
      return null;
    } catch (err) {
      const message = errorMessage(err, "Could not save the default tax.");
      if (err.response?.status === 409 || /tax/i.test(message)) return { field: "tax_id", message };
      if (/value|percentage/i.test(message)) return { field: "value", message };
      return { message };
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`${endpoint}/${taxToDelete}`);
      setTaxes((prev) => prev.filter((t) => t.id !== taxToDelete));
      toast.success("Default tax deleted.");
    } catch (err) {
      toast.error(errorMessage(err, "Failed to delete the default tax."));
    } finally {
      setTaxToDelete(null);
    }
  };

  if (loading) return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  const editingTax =
    action === "edit" ? taxes.find((t) => String(t.id) === String(editId)) : null;
  const inForm = action === "add" || Boolean(editingTax);

  return (
    <>
      <div className="md:w-2/3">
        <div className="flex justify-between items-center gap-3 mb-6">
          <div className="flex items-center gap-3">
            <BackButton onClick={inForm ? () => setAction(null) : undefined} />
            <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">{title}</h1>
          </div>
          {canEdit && !inForm && (
            <button onClick={() => setAction("add")} className={btn("primary", "sm")}>
              Add Default Tax
            </button>
          )}
        </div>

        {error && <p className="text-danger-600 text-sm mb-3">{error}</p>}

        {inForm ? (
          <DefaultTaxForm
            key={editingTax?.id ?? "new"}
            party={party}
            taxToEdit={editingTax}
            taxes={accountTaxes}
            usedTaxIds={new Set(taxes.map((t) => t.tax_id))}
            onSave={handleSave}
            onCancel={() => setAction(null)}
          />
        ) : (
          <SystemValueList
            items={taxes}
            columns={COLUMNS}
            onEdit={(tax) => setAction("edit", tax.id)}
            onDelete={(id) => setTaxToDelete(id)}
            showStatus={false}
            showActions={canEdit}
            emptyMessage="No default taxes added yet."
          />
        )}
      </div>

      {taxToDelete && (
        <ConfirmModal
          title="Confirm Deletion"
          message="Are you sure you want to delete this default tax? It will no longer be applied by default."
          onCancel={() => setTaxToDelete(null)}
          onConfirm={handleDelete}
          confirmText="Delete"
        />
      )}
    </>
  );
};

export default DefaultTaxesPage;